import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { fmtUSD } from '../utils/currency';
import { toTitleCase } from '../utils/textFormat';
import { getApiErrorMessage } from '../utils/apiError';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchSelect } from '../components/ui/search-select';
import { ArrowLeft, SpinnerGap, Info, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const parseDiscountLookup = (data) => {
  if (!data) return { records: [], resolved: null };
  if (Array.isArray(data)) return { records: data, resolved: null };
  return { records: data.records || [], resolved: data.resolved || null };
};

const discountFromLookup = (data) => {
  const { records, resolved } = parseDiscountLookup(data);
  if (resolved && ((resolved.discount_pct || 0) > 0 || (resolved.discount_amount_per_hr || 0) > 0 || (resolved.fixed_bill_rate || 0) > 0)) {
    return { ...resolved, company: records[0]?.company, vendor: records[0]?.vendor, end_client: records[0]?.end_client };
  }
  if (records.length > 0) {
    return records.reduce((a, b) => (b.discount_pct || 0) > (a.discount_pct || 0) ? b : a, records[0]);
  }
  return null;
};

const effectiveFixedBillRate = (bill, discount) => {
  if (!bill || !discount) return bill;
  if (discount.fixed_bill_rate && Number(discount.fixed_bill_rate) > 0 && Number(discount.fixed_bill_rate) < bill) {
    return Number(discount.fixed_bill_rate);
  }
  const flat = Number(discount.discount_amount_per_hr || 0);
  if (flat > 0) return bill - flat;
  const pct = Number(discount.discount_pct || 0);
  if (pct > 0) return bill - (bill * pct / 100);
  return bill;
};

const SALES_CATEGORIES = [
  { value: 'new_cpt_opt', label: 'New CPT/OPT (Using with support)' },
  { value: 'sales_recruiting_direct', label: 'Sales Recruiting (Direct Submissions — no extra sourcing support)' },
  { value: 'existing_cpt_opt', label: 'Existing CPT/OPT' },
  { value: 'any_new_h1t', label: 'Any New H1 Transfer' },
  { value: 'existing_h1_gc_usc', label: 'Existing H1 / GC / USC' },
  { value: 'low_end_existing', label: 'Low End - Existing Bench' },
  { value: 'low_end_new', label: 'Low End - New Candidate' },
];

// ==================== EXACT INCENTIVE MATRIX (matches backend) ====================
const SALES_INCENTIVE_MATRIX = {
  new_cpt_opt: [
    { min: 30, max: 999, total: 41750, direct: 35000, sr: 28000, src: 7000, sl: 3000, sm: 1500, srcl: 1500, srcm: 750 },
    { min: 25, max: 30, total: 36750, direct: 30000, sr: 24000, src: 6000, sl: 3000, sm: 1500, srcl: 1500, srcm: 750 },
    { min: 20, max: 25, total: 30550, direct: 25000, sr: 20000, src: 5000, sl: 2500, sm: 1250, srcl: 1200, srcm: 600 },
    { min: 15, max: 20, total: 24500, direct: 20000, sr: 16000, src: 4000, sl: 2000, sm: 1000, srcl: 1000, srcm: 500 },
    { min: 10, max: 15, total: 18450, direct: 15000, sr: 12000, src: 3000, sl: 1500, sm: 750, srcl: 800, srcm: 400 },
    { min: 0, max: 10, total: 12400, direct: 10000, sr: 8000, src: 2000, sl: 1000, sm: 500, srcl: 600, srcm: 300 },
  ],
  sales_recruiting_direct: [
    { min: 30, max: 999, total: 58600, direct: 50500, sr: 28000, src: 22500, sl: 2400, sm: 1200, srcl: 2500, srcm: 2000 },
    { min: 25, max: 30, total: 51100, direct: 44000, sr: 24000, src: 20000, sl: 2200, sm: 1100, srcl: 2000, srcm: 1800 },
    { min: 20, max: 25, total: 43200, direct: 37000, sr: 20000, src: 17000, sl: 2000, sm: 1000, srcl: 1700, srcm: 1500 },
    { min: 15, max: 20, total: 34700, direct: 29500, sr: 16000, src: 13500, sl: 1600, sm: 800, srcl: 1500, srcm: 1300 },
    { min: 10, max: 15, total: 25800, direct: 22000, sr: 12000, src: 10000, sl: 1200, sm: 600, srcl: 1000, srcm: 1000 },
    { min: 0, max: 10, total: 17600, direct: 15000, sr: 8000, src: 7000, sl: 800, sm: 400, srcl: 700, srcm: 700 },
  ],
  existing_cpt_opt: [
    { min: 30, max: 999, total: 31360, direct: 28000, sr: 0, src: 0, sl: 2240, sm: 1120, srcl: 0, srcm: 0 },
    { min: 25, max: 30, total: 26880, direct: 24000, sr: 0, src: 0, sl: 1920, sm: 960, srcl: 0, srcm: 0 },
    { min: 20, max: 25, total: 22400, direct: 20000, sr: 0, src: 0, sl: 1600, sm: 800, srcl: 0, srcm: 0 },
    { min: 15, max: 20, total: 17920, direct: 16000, sr: 0, src: 0, sl: 1280, sm: 640, srcl: 0, srcm: 0 },
    { min: 10, max: 15, total: 13440, direct: 12000, sr: 0, src: 0, sl: 960, sm: 480, srcl: 0, srcm: 0 },
    { min: 0, max: 10, total: 8960, direct: 8000, sr: 0, src: 0, sl: 640, sm: 320, srcl: 0, srcm: 0 },
  ],
  any_new_h1t: [
    { min: 30, max: 999, total: 45750, direct: 30000, sr: 24000, src: 15000, sl: 3000, sm: 1500, srcl: 1500, srcm: 750 },
    { min: 25, max: 30, total: 38750, direct: 25000, sr: 20000, src: 12000, sl: 3000, sm: 1500, srcl: 1500, srcm: 750 },
    { min: 20, max: 25, total: 30800, direct: 20000, sr: 16000, src: 10000, sl: 2000, sm: 1000, srcl: 1200, srcm: 600 },
    { min: 15, max: 20, total: 23750, direct: 15000, sr: 12000, src: 8000, sl: 1500, sm: 750, srcl: 1000, srcm: 500 },
    { min: 10, max: 15, total: 14325, direct: 7500, sr: 6000, src: 6000, sl: 750, sm: 375, srcl: 800, srcm: 400 },
  ],
  existing_h1_gc_usc: [
    { min: 30, max: 999, total: 29568, direct: 26400, sr: 0, src: 0, sl: 2112, sm: 1056, srcl: 0, srcm: 0 },
    { min: 25, max: 30, total: 18480, direct: 16500, sr: 0, src: 0, sl: 1320, sm: 660, srcl: 0, srcm: 0 },
    { min: 20, max: 25, total: 14784, direct: 13200, sr: 0, src: 0, sl: 1056, sm: 528, srcl: 0, srcm: 0 },
    { min: 15, max: 20, total: 12320, direct: 11000, sr: 0, src: 0, sl: 880, sm: 440, srcl: 0, srcm: 0 },
    { min: 10, max: 15, total: 7392, direct: 6600, sr: 0, src: 0, sl: 528, sm: 264, srcl: 0, srcm: 0 },
  ],
  low_end_existing: [
    { min: 10, max: 999, total: 9375, direct: 7500, sr: 0, src: 0, sl: 1250, sm: 625, srcl: 0, srcm: 0 },
    { min: 0, max: 10, total: 4000, direct: 3000, sr: 0, src: 0, sl: 750, sm: 250, srcl: 0, srcm: 0 },
  ],
  low_end_new: [
    { min: 10, max: 999, total: 9500, direct: 8000, sr: 4000, src: 4000, sl: 0, sm: 0, srcl: 1000, srcm: 500 },
    { min: 0, max: 10, total: 6000, direct: 5000, sr: 2500, src: 2500, sl: 0, sm: 0, srcl: 750, srcm: 250 },
  ],
};

const RECRUITING_INCENTIVE_MATRIX = {
  existing_bench: [
    { min: 30, max: 999, total: 20700, recruiter: 18000, lead: 1800, manager: 900 },
    { min: 25, max: 30, total: 17250, recruiter: 15000, lead: 1500, manager: 750 },
    { min: 20, max: 25, total: 13800, recruiter: 12000, lead: 1200, manager: 600 },
    { min: 15, max: 20, total: 9200, recruiter: 8000, lead: 800, manager: 400 },
    { min: 10, max: 15, total: 6900, recruiter: 6000, lead: 600, manager: 300 },
    { min: 0, max: 10, total: 4600, recruiter: 4000, lead: 400, manager: 200 },
  ],
  new_bench: [
    { min: 30, max: 999, total: 28750, recruiter: 25000, lead: 2500, manager: 1250 },
    { min: 25, max: 30, total: 23000, recruiter: 20000, lead: 2000, manager: 1000 },
    { min: 20, max: 25, total: 20700, recruiter: 18000, lead: 1800, manager: 900 },
    { min: 15, max: 20, total: 13800, recruiter: 12000, lead: 1200, manager: 600 },
    { min: 10, max: 15, total: 11500, recruiter: 10000, lead: 1000, manager: 500 },
    { min: 0, max: 10, total: 9200, recruiter: 8000, lead: 800, manager: 400 },
  ],
  core_recruiting: [
    { min: 30, max: 999, total: 40250, recruiter: 35000, lead: 3500, manager: 1750 },
    { min: 25, max: 30, total: 28750, recruiter: 25000, lead: 2500, manager: 1250 },
    { min: 20, max: 25, total: 23000, recruiter: 20000, lead: 2000, manager: 1000 },
    { min: 15, max: 20, total: 17250, recruiter: 15000, lead: 1500, manager: 750 },
    { min: 10, max: 15, total: 14375, recruiter: 12500, lead: 1250, manager: 625 },
  ],
  c2c: [
    { min: 25, max: 999, total: 40250, recruiter: 35000, lead: 3500, manager: 1750 },
    { min: 20, max: 25, total: 28750, recruiter: 25000, lead: 2500, manager: 1250 },
    { min: 15, max: 20, total: 23000, recruiter: 20000, lead: 2000, manager: 1000 },
    { min: 10, max: 15, total: 17250, recruiter: 15000, lead: 1500, manager: 750 },
    { min: 5, max: 10, total: 14375, recruiter: 12500, lead: 1250, manager: 625 },
    { min: 0, max: 5, total: 6200, recruiter: 5000, lead: 800, manager: 400 },
  ],
  low_end_existing: [
    { min: 10, max: 999, total: 9375, recruiter: 7500, lead: 1250, manager: 625 },
    { min: 0, max: 10, total: 4000, recruiter: 3000, lead: 700, manager: 350 },
  ],
  low_end_new: [
    { min: 10, max: 999, total: 9375, recruiter: 7500, lead: 1250, manager: 625 },
    { min: 0, max: 10, total: 5100, recruiter: 4000, lead: 700, manager: 400 },
  ],
};

const RECRUITING_CATEGORIES = [
  { value: 'existing_bench', label: 'Any Existing Bench' },
  { value: 'new_bench', label: 'Any New Bench' },
  { value: 'core_recruiting', label: 'Core Recruiting' },
  { value: 'c2c', label: 'C2C' },
  { value: 'low_end_existing', label: 'Low End - Existing Bench' },
  { value: 'low_end_new', label: 'Low End - New Candidate' },
  { value: 'internal_candidate', label: 'Internal Candidate (Flat ₹7,500)' },
  { value: 'fulltime', label: 'Full-time Placement (Flat ₹5,000)' },
];

const formatCurrency = (amount) => `₹${amount?.toLocaleString('en-IN') || 0}`;

// Categories where sourcing support is N/A (existing bench categories — sr=0, src=0 in matrix)
const NO_SOURCING_CATEGORIES = ['existing_cpt_opt', 'existing_h1_gc_usc', 'low_end_existing'];

const SplitRow = ({ label, amount, name, color, showAmount = true, pctLabel }) => {
  if (!showAmount && !name) return null;
  if (showAmount && !amount && amount !== 0) return null;
  if (showAmount && amount === 0) return null;
  const bgColors = { blue: 'bg-blue-100 text-blue-700', teal: 'bg-teal-100 text-teal-700', purple: 'bg-purple-100 text-purple-700', indigo: 'bg-indigo-100 text-indigo-700', cyan: 'bg-cyan-100 text-cyan-700', sky: 'bg-sky-100 text-sky-700', violet: 'bg-violet-100 text-violet-700', emerald: 'bg-emerald-100 text-emerald-700', amber: 'bg-amber-100 text-amber-700' };
  return (
    <div className="flex items-center justify-between text-sm py-1" data-testid={`split-preview-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2">
        <span className={`w-5 h-5 ${bgColors[color] || 'bg-slate-100 text-slate-700'} rounded-full flex items-center justify-center text-[10px] font-bold`}>
          {label.charAt(0)}
        </span>
        <div>
          <span className="text-slate-600">{label}</span>
          {name && <span className="text-xs text-slate-400 ml-1">({name})</span>}
        </div>
      </div>
      {showAmount ? (
        <span className="font-medium text-slate-800">{formatCurrency(amount)}</span>
      ) : (
        <span className="text-xs text-slate-400 italic">{pctLabel || 'Name only'}</span>
      )}
    </div>
  );
};

const NewPlacementPage = () => {
  const navigate = useNavigate();
  const { user, isRecruiter, isLead, isManager, isSuperadmin, isCeo } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState([]);

  const isNativeRecruiting = ['technical_recruiter', 'recruiting_lead', 'recruiting_manager', 'recruiting_assistant_manager'].includes(user?.role);
  const isNativeSales = ['sales_recruiter', 'sales_lead', 'sales_manager', 'sales_assistant_manager'].includes(user?.role);
  const isDualRecruiter = user?.role === 'sales_technical_recruiter';
  const canSwitchModule = isSuperadmin || isCeo || user?.role === 'onsite_manager' || isDualRecruiter;
  const [selectedModule, setSelectedModule] = useState(isNativeRecruiting ? 'recruiting' : 'sales');
  const isRecruitingModule = selectedModule === 'recruiting';
  const [customCategories, setCustomCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/categories`, { withCredentials: true })
      .then(r => setCustomCategories(r.data || []))
      .catch(() => {});
  }, []);

  const categories = (() => {
    const base = isRecruitingModule ? RECRUITING_CATEGORIES : SALES_CATEGORIES;
    const custom = customCategories.filter(c =>
      c.module === selectedModule || c.module === 'both'
    ).filter(c => !base.some(b => b.value === c.value));
    return [...base, ...custom];
  })();

  const [formData, setFormData] = useState({
    candidate_first_name: '',
    candidate_last_name: '',
    job_title: '',
    industry: '',
    category: '',
    company: '',
    vendor: '',
    vendor_type: 'old',
    client: '',
    client_type: 'existing',
    bill_rate: '',
    pay_rate: '',
    sourcing_support: '',
    sourced_from: '',
    reference_note: '',
    employment_type: '',
    c2c_company: '',
    is_new_technology: false,
    new_technology_name: '',
    start_date: '',
    project_end_date: '',
    placement_month: new Date().getMonth() + 1,
    placement_year: new Date().getFullYear(),
    submission_notes: '',
    is_internal_candidate: false,
    is_fulltime: false,
    external_support: 'no',
    external_support_amount: '',
    external_support_name: '',
    external_support_portal: '',
    sixty_day_completed: '',
    // New entity flags — when true, the typed name goes through SA review
    is_new_vendor_pending: false,
    is_new_client_pending: false,
    is_new_msp_pending: false,
    // Recruiting "new client" toggle (recruiting module doesn't have vendor_type)
    recruiting_is_new_client: false,
    // SA/CEO creating on behalf of another user
    created_for_user_id: '',
  });

  const [splitShare, setSplitShare] = useState({ enabled: false, user_id: '', percentage: '' });
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [originalBillRate, setOriginalBillRate] = useState(0);
  const [additionalSplits, setAdditionalSplits] = useState([]);

  // Discount state
  const [discountCompanies, setDiscountCompanies] = useState([]);
  const [discountVendors, setDiscountVendors] = useState([]);
  const [discountEndClients, setDiscountEndClients] = useState([]);
  const [selectedDiscount, setSelectedDiscount] = useState({ company: '', vendor: '', end_client: '' });
  const [discountData, setDiscountData] = useState(null);

  // RTR upload
  const [rtrFile, setRtrFile] = useState(null);
  const [rtrUploading, setRtrUploading] = useState(false);
  const [rtrUploaded, setRtrUploaded] = useState(null);

  // Entity dropdowns (from admin)
  const [entityCompanies, setEntityCompanies] = useState([]);
  const [entityVendors, setEntityVendors] = useState([]);
  const [entityClients, setEntityClients] = useState([]);
  const [entityC2cCompanies, setEntityC2cCompanies] = useState([]);

  const [preview, setPreview] = useState(null);
  const [teamInfo, setTeamInfo] = useState({ lead: null, manager: null });

  const [allUsers, setAllUsers] = useState([]);
  // List of users SA/CEO can create placements on behalf of
  const [proxyTargetUsers, setProxyTargetUsers] = useState([]);
  // Vendor / client ownership lookup result for the staged-bonus preview banner
  const [vendorClaim, setVendorClaim] = useState(null);
  const [clientClaim, setClientClaim] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchTeamInfo();
    fetchDiscountCompanies();
    fetchEntityDropdowns();
    if (isSuperadmin || isCeo) {
      axios.get(`${API_URL}/api/users`, { withCredentials: true })
        .then(r => {
          const filtered = (r.data || []).filter(u => !['superadmin', 'ceo'].includes(u.role));
          setProxyTargetUsers(filtered);
        })
        .catch(() => setProxyTargetUsers([]));
    }
  }, [isSuperadmin, isCeo]);

  useEffect(() => {
    if (canSwitchModule) fetchTeamInfo();
  }, [selectedModule]);

  useEffect(() => {
    if (formData.bill_rate && formData.pay_rate && formData.category) {
      const bill = parseFloat(formData.bill_rate);
      const pay = parseFloat(formData.pay_rate);
      // Apply auto-looked-up discount % to get Fixed Bill Rate
      const discount = appliedDiscount || discountData;
      const fixedBillRate = effectiveFixedBillRate(bill, discount);
      const margin = fixedBillRate - pay;
      if (margin >= 0) {
        calculatePreview(margin);
      }
    }
  }, [formData.bill_rate, formData.pay_rate, formData.category, formData.is_new_technology, formData.is_internal_candidate, formData.is_fulltime, discountData, appliedDiscount]);

  useEffect(() => {
    const co = formData.company;
    const ve = formData.vendor;
    if (!co || !ve) return;
    const cl = formData.client;
    const billQ = formData.bill_rate ? `&bill_rate=${formData.bill_rate}` : '';
    const dateQ = formData.start_date ? `&start_date=${encodeURIComponent(formData.start_date)}` : '';
    axios.get(`${API_URL}/api/discounts/lookup?company=${encodeURIComponent(co)}&vendor=${encodeURIComponent(ve)}${cl ? `&end_client=${encodeURIComponent(cl)}` : ''}${billQ}${dateQ}`, { withCredentials: true })
      .then(res => setAppliedDiscount(discountFromLookup(res.data)))
      .catch(() => {});
  }, [formData.bill_rate, formData.start_date]);

  // Look up vendor / client ownership for the staged-bonus preview banner.
  // Debounced via the dependency on the trimmed name.
  useEffect(() => {
    const name = (formData.vendor || '').trim();
    if (!name || isRecruitingModule) { setVendorClaim(null); return; }
    let alive = true;
    axios.get(`${API_URL}/api/vendor-claims/lookup?vendor=${encodeURIComponent(name)}&kind=vendor`, { withCredentials: true })
      .then(r => { if (alive) setVendorClaim(r.data); })
      .catch(() => { if (alive) setVendorClaim(null); });
    return () => { alive = false; };
  }, [formData.vendor, isRecruitingModule]);

  useEffect(() => {
    const name = (formData.client || '').trim();
    if (!name || isRecruitingModule || formData.vendor_type !== 'new_client') { setClientClaim(null); return; }
    let alive = true;
    axios.get(`${API_URL}/api/vendor-claims/lookup?client=${encodeURIComponent(name)}&kind=client`, { withCredentials: true })
      .then(r => { if (alive) setClientClaim(r.data); })
      .catch(() => { if (alive) setClientClaim(null); });
    return () => { alive = false; };
  }, [formData.client, formData.vendor_type, isRecruitingModule]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/team-members`, { withCredentials: true });
      let list = response.data || [];
      // Ensure the current user can pick themselves as Sourcing Support — even if their
      // role isn't in the team-members endpoint (e.g. lead/manager creating a placement).
      if (user?.id && !list.some(u => u.id === user.id)) {
        list = [...list, { id: user.id, name: user.name, email: user.email, role: user.role, team: user.team || '' }];
      }
      setAllUsers(list);
      setUsers(list);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    }
  };

  const fetchTeamInfo = async () => {
    try {
      // Use team-members for basic info, but also try /api/users for leads if allowed
      let au = [];
      try {
        const response = await axios.get(`${API_URL}/api/users`, { withCredentials: true });
        au = response.data;
      } catch {
        // Fallback: recruiters can't access /api/users, use team-members
        const response = await axios.get(`${API_URL}/api/team-members`, { withCredentials: true });
        au = response.data;
      }
      
      if (selectedModule === 'sales' || ['sales_recruiter', 'sales_lead', 'sales_manager'].includes(user?.role)) {
        const salesLead = au.find(u => u.role === 'sales_lead');
        const salesManager = au.find(u => u.role === 'sales_manager');
        const sourcingRecruiter = au.find(u => u.role === 'technical_recruiter');
        const sourcingLead = au.find(u => u.role === 'recruiting_lead');
        const sourcingManager = au.find(u => u.role === 'recruiting_manager');
        setTeamInfo({ lead: salesLead, manager: salesManager, sourcingRecruiter, sourcingLead, sourcingManager });
      } else {
        const recruitingLead = au.find(u => u.role === 'recruiting_lead');
        const recruitingManager = au.find(u => u.role === 'recruiting_manager');
        setTeamInfo({ lead: recruitingLead, manager: recruitingManager });
      }
    } catch (error) {
      console.error('Failed to fetch team info:', error);
    }
  };

  const fetchDiscountCompanies = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/discounts/companies`, { withCredentials: true });
      setDiscountCompanies(res.data);
    } catch (e) { console.error('Failed to fetch discount companies:', e); }
  };

  const autoDetectEntityType = async (vendor, client, company) => {
    if (!vendor && !client) return;
    try {
      const params = new URLSearchParams();
      if (vendor) params.append('vendor', vendor);
      if (client) params.append('client', client);
      if (company) params.append('company', company);
      const res = await axios.get(`${API_URL}/api/entities/auto-detect?${params.toString()}`, { withCredentials: true });
      setFormData(prev => {
        const next = { ...prev };
        // API returns "existing" but form Select values are: new | existing_new | new_client | old
        if (vendor && res.data.vendor_type) {
          const mapped =
            res.data.vendor_type === 'existing' ? 'old'
            : res.data.vendor_type === 'existing_new' ? 'existing_new'
            : res.data.vendor_type === 'new' ? 'new'
            : prev.vendor_type;
          // Never overwrite an explicit new_client choice
          if (prev.vendor_type !== 'new_client') {
            next.vendor_type = mapped;
            next.is_new_vendor_pending = mapped === 'new';
          }
        }
        if (client && res.data.client_type) {
          next.client_type = res.data.client_type === 'existing' ? 'old' : res.data.client_type;
        }
        return next;
      });
    } catch (e) {
      console.error('Auto-detect failed:', e);
      if (e.response?.status === 401) {
        toast.error('Session expired. Please sign in again to load vendor/client options.');
      }
    }
  };

  const fetchEntityDropdowns = async () => {
    try {
      const [cRes, vRes, clRes, c2cRes] = await Promise.all([
        axios.get(`${API_URL}/api/entities/dropdown?entity_type=company`, { withCredentials: true }),
        axios.get(`${API_URL}/api/entities/dropdown?entity_type=vendor`, { withCredentials: true }),
        axios.get(`${API_URL}/api/entities/dropdown?entity_type=client`, { withCredentials: true }),
        axios.get(`${API_URL}/api/entities/dropdown?entity_type=c2c_company`, { withCredentials: true }),
      ]);
      setEntityCompanies(Array.isArray(cRes.data) ? cRes.data : []);
      setEntityVendors(Array.isArray(vRes.data) ? vRes.data : []);
      setEntityClients(Array.isArray(clRes.data) ? clRes.data : []);
      setEntityC2cCompanies(Array.isArray(c2cRes.data) ? c2cRes.data : []);
      if (!(cRes.data?.length) && !(vRes.data?.length)) {
        toast.error('Organization/vendor lists are empty. Contact admin if this persists.');
      }
    } catch (e) {
      console.error('Failed to fetch entities:', e);
      const detail = e.response?.data?.detail || e.message;
      if (e.response?.status === 401) {
        toast.error('Session expired — please sign in again. Organization and vendor lists could not load.');
      } else {
        toast.error(`Could not load Organization/Vendor lists: ${detail}`);
      }
      // Fallback: organizations endpoint for company names
      try {
        const orgRes = await axios.get(`${API_URL}/api/organizations`, { withCredentials: true });
        const names = (orgRes.data || []).map((o) => o.name || o).filter(Boolean);
        if (names.length) setEntityCompanies(names);
      } catch { /* ignore */ }
    }
  };


  const handleDiscountCompanyChange = async (company) => {
    setSelectedDiscount({ company, vendor: '', end_client: '' });
    setDiscountData(null);
    setDiscountVendors([]);
    setDiscountEndClients([]);
    if (!company) return;
    try {
      const res = await axios.get(`${API_URL}/api/discounts/vendors?company=${encodeURIComponent(company)}`, { withCredentials: true });
      setDiscountVendors(res.data);
    } catch (e) { console.error(e); }
  };

  const handleDiscountVendorChange = async (vendor) => {
    setSelectedDiscount(prev => ({ ...prev, vendor, end_client: '' }));
    setDiscountData(null);
    setDiscountEndClients([]);
    if (!vendor) return;
    try {
      const res = await axios.get(`${API_URL}/api/discounts/end-clients?company=${encodeURIComponent(selectedDiscount.company)}&vendor=${encodeURIComponent(vendor)}`, { withCredentials: true });
      setDiscountEndClients(res.data);
    } catch (e) { console.error(e); }
  };

  const handleDiscountEndClientChange = async (end_client) => {
    setSelectedDiscount(prev => ({ ...prev, end_client }));
    if (!end_client) { setDiscountData(null); return; }
    try {
      const res = await axios.get(`${API_URL}/api/discounts/lookup?company=${encodeURIComponent(selectedDiscount.company)}&vendor=${encodeURIComponent(selectedDiscount.vendor)}&end_client=${encodeURIComponent(end_client)}&bill_rate=${formData.bill_rate || 0}&start_date=${encodeURIComponent(formData.start_date || '')}`, { withCredentials: true });
      const d = discountFromLookup(res.data);
      if (d) {
        // Auto-calculate tenure discount based on start_date
        let tenureDiscount = 0;
        if (formData.start_date && (d.tenure_12m_pct || d.tenure_24m_pct)) {
          const start = new Date(formData.start_date);
          const now = new Date();
          const monthsElapsed = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
          if (monthsElapsed >= 24 && d.tenure_24m_pct) tenureDiscount = d.tenure_24m_pct;
          else if (monthsElapsed >= 12 && d.tenure_12m_pct) tenureDiscount = d.tenure_12m_pct;
        }
        setDiscountData({ ...d, calculated_tenure_discount: tenureDiscount });
      } else { setDiscountData(null); }
    } catch (e) { console.error(e); }
  };

  const MAX_RTR_SIZE_MB = 10;
  // Keep each request well below typical reverse-proxy body limits (nginx default 1MB)
  const RTR_CHUNK_SIZE = 700 * 1024;

  const uploadRtrInChunks = async (file) => {
    const totalChunks = Math.ceil(file.size / RTR_CHUNK_SIZE);
    let uploadId = '';
    for (let i = 0; i < totalChunks; i++) {
      const blob = file.slice(i * RTR_CHUNK_SIZE, Math.min((i + 1) * RTR_CHUNK_SIZE, file.size));
      const fd = new FormData();
      fd.append('chunk', blob, 'chunk.bin');
      fd.append('upload_id', uploadId);
      fd.append('chunk_index', String(i));
      fd.append('total_chunks', String(totalChunks));
      fd.append('filename', file.name);
      const res = await axios.post(`${API_URL}/api/upload/rtr/chunk`, fd, {
        withCredentials: true,
        timeout: 60000,
      });
      uploadId = res.data.upload_id || uploadId;
      if (res.data.complete) return res.data;
    }
    throw new Error('Upload did not complete. Please try again.');
  };

  const handleRtrUpload = async (file) => {
    if (!file) return;
    const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(`"${file.name}" is not a PDF. Only PDF files are allowed for RTR.`);
      return;
    }
    if (file.size === 0) {
      toast.error(`"${file.name}" is empty (0 KB). Please choose a valid PDF file.`);
      return;
    }
    if (file.size > MAX_RTR_SIZE_MB * 1024 * 1024) {
      toast.error(`"${file.name}" is ${sizeMB} MB — the maximum allowed size is ${MAX_RTR_SIZE_MB} MB. Please compress the PDF and try again.`);
      return;
    }
    setRtrUploading(true);
    try {
      let data;
      if (file.size <= RTR_CHUNK_SIZE) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_URL}/api/upload/rtr`, fd, {
          withCredentials: true,
          timeout: 120000,
        });
        data = res.data;
      } else {
        // Larger files are sent in small chunks so no single request hits
        // the server's request body size limit.
        data = await uploadRtrInChunks(file);
      }
      setRtrUploaded(data);
      toast.success(`RTR uploaded successfully (${file.name}, ${sizeMB} MB)`);
    } catch (e) {
      let msg;
      if (e.response?.status === 413) {
        msg = `"${file.name}" (${sizeMB} MB) was rejected by the server because it exceeds the server's upload size limit. Please compress the PDF or contact the administrator to increase the limit.`;
      } else if (e.code === 'ECONNABORTED') {
        msg = `Uploading "${file.name}" (${sizeMB} MB) timed out. Please check your internet connection and try again.`;
      } else if (e.response) {
        msg = getApiErrorMessage(e);
      } else if (e.request) {
        msg = `"${file.name}" (${sizeMB} MB) could not be uploaded — the server rejected the request before processing it. This usually means the file exceeds the server's upload size limit. Try compressing the PDF to a smaller size, or contact the administrator.`;
      } else {
        msg = e.message || 'Unexpected error. Please try again.';
      }
      toast.error(`Failed to upload RTR: ${msg}`, { duration: 8000 });
    } finally {
      setRtrUploading(false);
    }
  };

  const calculatePreview = (margin) => {
    const category = formData.category;
    if (!category) return;

    if (isRecruitingModule) {
      // Recruiting: flat rates for special categories
      if (formData.is_internal_candidate || category === 'internal_candidate') {
        setPreview({ margin: margin.toFixed(2), estimatedTotal: 7500, recruiterPayout: 7500, isFlat: true, splits: { recruiter: 7500, lead: 0, manager: 0 } });
        return;
      }
      if (formData.is_fulltime || category === 'fulltime') {
        setPreview({ margin: margin.toFixed(2), estimatedTotal: 5000, recruiterPayout: 5000, isFlat: true, splits: { recruiter: 5000, lead: 0, manager: 0 } });
        return;
      }
      const isLowEnd = parseFloat(formData.bill_rate) <= 40;
      let effectiveCategory = category;
      if (isLowEnd && !['fulltime', 'internal_candidate', 'c2c'].includes(category)) {
        if (category === 'low_end_existing' || category === 'existing_bench' || category.includes('existing')) {
          effectiveCategory = 'low_end_existing';
        } else {
          effectiveCategory = 'low_end_new';
        }
      }
      const matrix = RECRUITING_INCENTIVE_MATRIX[effectiveCategory] || [];
      let matched = null;
      for (const tier of matrix) {
        if ((tier.max === 999 && margin > tier.min) || (tier.min < margin && margin <= tier.max) || (tier.min === 0 && margin <= tier.max)) {
          matched = tier;
          break;
        }
      }
      if (matched) {
        setPreview({
          margin: margin.toFixed(2),
          estimatedTotal: matched.total,
          recruiterPayout: matched.recruiter,
          isFlat: false,
          isLowEnd,
          effectiveCategory,
          splits: { recruiter: matched.recruiter, lead: matched.lead, manager: matched.manager },
        });
      } else {
        setPreview({ margin: margin.toFixed(2), estimatedTotal: 0, recruiterPayout: 0, isFlat: false, isLowEnd, effectiveCategory, splits: {} });
      }
    } else {
      // Sales: use exact matrix based on category
      const isLowEnd = parseFloat(formData.bill_rate) <= 40;
      let effectiveCategory = category;
      if (isLowEnd) {
        effectiveCategory = ['existing_cpt_opt', 'existing_h1_gc_usc'].includes(category) ? 'low_end_existing' : 'low_end_new';
      }
      const matrix = SALES_INCENTIVE_MATRIX[effectiveCategory] || [];
      let matched = null;
      for (const tier of matrix) {
        if ((tier.max === 999 && margin > tier.min) || (tier.min < margin && margin <= tier.max) || (tier.min === 0 && margin <= tier.max)) {
          matched = tier;
          break;
        }
      }
      if (matched) {
        let result = { ...matched };
        setPreview({
          margin: margin.toFixed(2),
          estimatedTotal: result.total,
          isLowEnd,
          effectiveCategory,
          splits: {
            sales_recruiter: result.sr,
            sourcing_recruiter: result.src,
            sales_lead: result.sl,
            sales_manager: result.sm,
            sourcing_lead: result.srcl,
            sourcing_manager: result.srcm,
          },
          totalDirect: result.direct,
        });
      } else {
        setPreview({ margin: margin.toFixed(2), estimatedTotal: 0, isLowEnd, splits: {} });
      }
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-set flags for special recruiting categories
      if (field === 'category') {
        if (value === 'internal_candidate') {
          updated.is_internal_candidate = true;
          updated.is_fulltime = false;
        } else if (value === 'fulltime') {
          updated.is_fulltime = true;
          updated.is_internal_candidate = false;
        } else {
          updated.is_internal_candidate = false;
          updated.is_fulltime = false;
        }
        // Clear sourcing support for categories that don't support it
        if (NO_SOURCING_CATEGORIES.includes(value)) {
          updated.sourcing_support = '';
        }
        // Clear employment_type if category is not sales_recruiting_direct
        if (value !== 'sales_recruiting_direct') {
          updated.employment_type = '';
          updated.c2c_company = '';
        }
      }
      // Auto-detect vendor/client type when selected
      if (field === 'vendor') {
        autoDetectEntityType(value, '', updated.company);
      }
      if (field === 'client') {
        autoDetectEntityType('', value, updated.company);
      }
      if (field === 'company' && updated.vendor) {
        autoDetectEntityType(updated.vendor, updated.client, value);
      }
      
      // Auto-lookup discount when company + vendor + client are set
      if (['company', 'vendor', 'client'].includes(field)) {
        const co = field === 'company' ? value : updated.company;
        const ve = field === 'vendor' ? value : updated.vendor;
        const cl = field === 'client' ? value : updated.client;
        if (co && ve) {
          const billQ = formData.bill_rate ? `&bill_rate=${formData.bill_rate}` : '';
          const dateQ = formData.start_date ? `&start_date=${encodeURIComponent(formData.start_date)}` : '';
          axios.get(`${API_URL}/api/discounts/lookup?company=${encodeURIComponent(co)}&vendor=${encodeURIComponent(ve)}${cl ? `&end_client=${encodeURIComponent(cl)}` : ''}${billQ}${dateQ}`, { withCredentials: true })
            .then(res => {
              setAppliedDiscount(discountFromLookup(res.data));
            })
            .catch(() => setAppliedDiscount(null));
        }
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e, saveAsDraft = true) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Mandatory field validation
    if (!formData.candidate_first_name.trim()) { setError('Candidate first name is required'); setLoading(false); return; }
    if (!formData.candidate_last_name.trim()) { setError('Candidate last name is required'); setLoading(false); return; }
    if (!formData.category) { setError('Category is required'); setLoading(false); return; }
    if (!formData.company) { setError('Organization/Entity is required'); setLoading(false); return; }
    if (!formData.sourced_from) { setError('Sourced From is required — select the portal where this candidate was sourced'); setLoading(false); return; }
    if (!rtrUploaded?.filename) {
      setError(isRecruitingModule ? 'Candidate RTR (PDF) is required' : 'Vendor RTR (PDF) is required');
      setLoading(false);
      return;
    }
    if (!formData.vendor && !isRecruitingModule) { setError('Vendor is required'); setLoading(false); return; }
    if (!formData.client) { setError('Client / End Client is required'); setLoading(false); return; }
    if (!formData.start_date) { setError('Start date is required for all placements'); setLoading(false); return; }
    if (!formData.bill_rate || !formData.pay_rate) { setError('Bill rate and pay rate are required'); setLoading(false); return; }
    if ((isSuperadmin || isCeo) && !formData.created_for_user_id) {
      setError('Please select the user this placement is for (Placement For). Super Admin / CEO is a role, not a placement owner.');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        ...formData,
        candidate_first_name: formData.candidate_first_name,
        candidate_last_name: formData.candidate_last_name,
        company: formData.company,
        bill_rate: parseFloat(formData.bill_rate),
        pay_rate: parseFloat(formData.pay_rate),
        client_type: formData.client_type,
        sourcing_support: formData.sourcing_support || null,
        sourced_from: formData.sourced_from || null,
        reference_note: formData.sourced_from === 'reference' ? formData.reference_note : null,
        employment_type: formData.employment_type || null,
        c2c_company: formData.c2c_company || null,
        new_technology_name: formData.is_new_technology ? formData.new_technology_name : null,
        start_date: formData.start_date || null,
        placement_month: formData.placement_month ? parseInt(formData.placement_month) : null,
        placement_year: formData.placement_year ? parseInt(formData.placement_year) : null,
        submission_notes: formData.submission_notes || null,
        split_share: splitShare.enabled && splitShare.user_id && splitShare.percentage
          ? { user_id: splitShare.user_id, percentage: parseFloat(splitShare.percentage) }
          : null,
        additional_splits: additionalSplits.filter(s => s.user_id && s.percentage > 0).map(s => ({
          user_id: s.user_id,
          percentage: parseFloat(s.percentage),
        })),
        // External support
        external_support: formData.external_support === 'yes',
        external_support_amount: formData.external_support === 'yes' ? parseFloat(formData.external_support_amount) || 0 : 0,
        external_support_name: formData.external_support === 'yes' ? formData.external_support_name || null : null,
        external_support_portal: formData.external_support === 'yes' ? formData.external_support_portal || null : null,
        // RTR
        rtr_filename: rtrUploaded?.filename || null,
        // 60-day completion
        sixty_day_completed: formData.sixty_day_completed || null,
        // Module override
        module: selectedModule,
        // New entity pending flags (read in backend on SA approve)
        is_new_vendor_pending: !!formData.is_new_vendor_pending,
        is_new_client_pending: !!formData.is_new_client_pending,
        is_new_msp_pending: !!formData.is_new_msp_pending,
        // SA / CEO on-behalf-of attribution
        created_for_user_id: (isSuperadmin || isCeo) ? (formData.created_for_user_id || null) : null,
      };

      const response = await axios.post(`${API_URL}/api/placements`, payload, { withCredentials: true });

      if (!saveAsDraft) {
        await axios.post(`${API_URL}/api/placements/${response.data.id}/submit?submission_notes=${encodeURIComponent(formData.submission_notes || '')}`, {}, { withCredentials: true });
      }

      navigate('/dashboard/placements');
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired (Not authenticated). Please sign out and sign in again, then retry.');
        toast.error('Session expired — please sign in again.');
      } else {
        setError(getApiErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const canCreatePlacement = isRecruiter || isLead || isManager || isSuperadmin || isCeo;

  if (!canCreatePlacement) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">You don't have permission to create placements.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard/placements')} data-testid="back-btn">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']">New Placement</h1>
          <p className="text-slate-500">
            {isRecruitingModule ? 'Recruiting Teams' : 'Sales Teams'} — Enter placement details to calculate incentives
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardContent className="p-6">
            <form onSubmit={(e) => { e.preventDefault(); }} className="space-y-8">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
                  {error}
                </div>
              )}

              {/* Module Selector */}
              <div className="space-y-2" data-testid="module-selector">
                {canSwitchModule ? (
                  <>
                    <p className="text-xs text-slate-500">Choose the department this placement belongs to.</p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => { setSelectedModule('sales'); handleChange('category', ''); }}
                        className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${selectedModule === 'sales' ? 'bg-blue-600 text-white shadow-md' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        data-testid="module-btn-sales"
                      >
                        Sales Teams
                      </button>
                      <button
                        type="button"
                        onClick={() => { setSelectedModule('recruiting'); handleChange('category', ''); }}
                        className={`px-4 py-2 rounded-full text-xs font-semibold transition-all ${selectedModule === 'recruiting' ? 'bg-teal-600 text-white shadow-md' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        data-testid="module-btn-recruiting"
                      >
                        Recruiting Teams
                      </button>
                    </div>
                  </>
                ) : (
                  <div className={`inline-flex items-center px-4 py-2 rounded-full text-xs font-semibold ${isRecruitingModule ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700'}`}>
                    {isRecruitingModule ? 'Recruiting Teams' : 'Sales Teams'}
                  </div>
                )}
              </div>

              {/* Placement For — visible only to Superadmin / CEO (they aren't employees, attribute to actual user) */}
              {(isSuperadmin || isCeo) && (
                <div className="space-y-2 border-t border-slate-100 pt-6" data-testid="placement-for-block">
                  <Label htmlFor="created_for_user_id" className="text-amber-700">Placement For (User) *</Label>
                  <p className="text-xs text-slate-500 -mt-1">As {isSuperadmin ? 'Super Admin' : 'CEO'}, select the recruiter / lead / manager this placement belongs to. Their name will be attributed as the creator and they will receive the credit & incentives.</p>
                  <Select value={formData.created_for_user_id} onValueChange={(v) => handleChange('created_for_user_id', v)}>
                    <SelectTrigger data-testid="select-placement-for" className="border-amber-300">
                      <SelectValue placeholder="Select user this placement is for" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {proxyTargetUsers.map(u => (
                        <SelectItem key={u.id} value={u.id} data-testid={`placement-for-${u.id}`}>
                          {u.name} ({(u.role || '').replace(/_/g, ' ')}{u.team ? ` · ${u.team}` : ''})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Candidate Info */}
              <div className="space-y-4 border-t border-slate-100 pt-6">
                <h3 className="font-semibold text-slate-900">Candidate Information</h3>
                <p className="text-xs text-slate-500 -mt-2">Basic details about the candidate being placed. First name, last name, and category are required.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="candidate_first_name">First Name *</Label>
                    <Input
                      id="candidate_first_name"
                      value={formData.candidate_first_name}
                      onChange={(e) => handleChange('candidate_first_name', e.target.value)}
                      onBlur={(e) => handleChange('candidate_first_name', toTitleCase(e.target.value))}
                      placeholder="First name"
                      required
                      data-testid="input-candidate-first-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="candidate_last_name">Last Name *</Label>
                    <Input
                      id="candidate_last_name"
                      value={formData.candidate_last_name}
                      onChange={(e) => handleChange('candidate_last_name', e.target.value)}
                      onBlur={(e) => handleChange('candidate_last_name', toTitleCase(e.target.value))}
                      placeholder="Last name"
                      required
                      data-testid="input-candidate-last-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="job_title">Job Title</Label>
                    <Input
                      id="job_title"
                      value={formData.job_title}
                      onChange={(e) => handleChange('job_title', e.target.value)}
                      onBlur={(e) => handleChange('job_title', toTitleCase(e.target.value))}
                      placeholder="e.g. Software Engineer, Data Analyst"
                      data-testid="input-job-title"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Select value={formData.industry} onValueChange={(v) => handleChange('industry', v)}>
                      <SelectTrigger data-testid="select-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                      <SelectContent>
                        {["Medical Devices", "Bio-tech", "Pharma", "Automotive", "Manufacturing", "Banking", "Financial"].map(i => (
                          <SelectItem key={i} value={i}>{i}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Category *</Label>
                    <Select value={formData.category} onValueChange={(v) => handleChange('category', v)} required>
                      <SelectTrigger data-testid="select-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400">Determines visa type, incentive tier, and approval workflow. Custom categories can be added by admin.</p>
                  </div>

                  {/* Employment Type dropdown — only for Sales Recruiting (Direct) */}
                  {formData.category === 'sales_recruiting_direct' && (
                    <div className="space-y-2">
                      <Label>Employment Type *</Label>
                      <Select value={formData.employment_type} onValueChange={(v) => {
                        handleChange('employment_type', v);
                        if (v !== 'c2c') handleChange('c2c_company', '');
                      }}>
                        <SelectTrigger data-testid="select-employment-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="w2">W2</SelectItem>
                          <SelectItem value="c2c">C2C</SelectItem>
                          <SelectItem value="c2c_self">C2C Self</SelectItem>
                          <SelectItem value="1099">1099</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* C2C Company dropdown — only when C2C is selected */}
                  {formData.category === 'sales_recruiting_direct' && formData.employment_type === 'c2c' && (
                    <div className="space-y-2">
                      <Label>C2C Company *</Label>
                      <SearchSelect
                        value={formData.c2c_company}
                        onValueChange={(v) => handleChange('c2c_company', v)}
                        options={entityC2cCompanies.length > 0 ? entityC2cCompanies : entityCompanies}
                        placeholder="Search C2C company..."
                        data-testid="select-c2c-company"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Company/Vendor/Client Info */}
              <div className="space-y-4 border-t border-slate-100 pt-6">
                <h3 className="font-semibold text-slate-900">{isRecruitingModule ? 'MSP & Client' : 'Organization, Vendor & Client'}</h3>
                <p className="text-xs text-slate-500 -mt-2">{isRecruitingModule ? 'Select the MSP and Client for this recruiting placement.' : 'Select the entity, vendor, and end client. These also determine vendor discount if applicable.'}</p>
                
                {/* Company (Entity/Organization) */}
                <div className="space-y-2">
                  <Label htmlFor="company">Organization / Entity *</Label>
                  <SearchSelect
                    id="company"
                    value={formData.company}
                    onValueChange={(v) => handleChange('company', v)}
                    options={entityCompanies}
                    placeholder="Search organization..."
                    minChars={0}
                    data-testid="select-company"
                  />
                  {entityCompanies.length === 0 && (
                    <p className="text-[11px] text-amber-600">No organizations loaded. Try refreshing the page or signing in again.</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {isRecruitingModule ? (
                    <>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="vendor">MSP</Label>
                          <label className="flex items-center gap-1 text-[11px] text-amber-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!formData.is_new_msp_pending}
                              onChange={(e) => {
                                handleChange('is_new_msp_pending', e.target.checked);
                                // Keep typed MSP name when enabling New MSP
                              }}
                              data-testid="toggle-new-msp"
                            />
                            New MSP
                          </label>
                        </div>
                        {formData.is_new_msp_pending ? (
                          <>
                            <Input
                              id="vendor"
                              value={formData.vendor}
                              onChange={(e) => handleChange('vendor', e.target.value)}
                              onBlur={(e) => handleChange('vendor', toTitleCase(e.target.value))}
                              placeholder="Type new MSP name"
                              data-testid="input-new-msp"
                            />
                            <p className="text-[11px] text-amber-600">⚠ Pending Super Admin review.</p>
                          </>
                        ) : (
                          <SearchSelect
                            id="vendor"
                            value={formData.vendor}
                            onValueChange={(v) => handleChange('vendor', v)}
                            onCreateOption={(name) => {
                              handleChange('vendor', toTitleCase(name));
                              handleChange('is_new_msp_pending', true);
                            }}
                            options={entityVendors}
                            placeholder="Search MSP..."
                            allowCreate
                            createLabel="Add as new MSP"
                            data-testid="select-vendor"
                          />
                        )}
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label htmlFor="client">Client *</Label>
                          <label className="flex items-center gap-1 text-[11px] text-amber-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!formData.is_new_client_pending}
                              onChange={(e) => {
                                handleChange('is_new_client_pending', e.target.checked);
                                // Keep typed client name when enabling New Client
                              }}
                              data-testid="toggle-new-client-recruiting"
                            />
                            New Client
                          </label>
                        </div>
                        {formData.is_new_client_pending ? (
                          <>
                            <Input
                              id="client"
                              value={formData.client}
                              onChange={(e) => handleChange('client', e.target.value)}
                              onBlur={(e) => handleChange('client', toTitleCase(e.target.value))}
                              placeholder="Type new client name"
                              data-testid="input-new-client-recruiting"
                            />
                            <p className="text-[11px] text-amber-600">⚠ Pending Super Admin review.</p>
                          </>
                        ) : (
                          <SearchSelect
                            id="client"
                            value={formData.client}
                            onValueChange={(v) => handleChange('client', v)}
                            onCreateOption={(name) => {
                              handleChange('client', toTitleCase(name));
                              handleChange('is_new_client_pending', true);
                            }}
                            options={entityClients}
                            placeholder="Search client..."
                            allowCreate
                            createLabel="Add as new client"
                            data-testid="select-client"
                          />
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="end_client">End Client</Label>
                        <SearchSelect
                          id="end_client"
                          value={formData.end_client === 'none' ? '' : (formData.end_client || '')}
                          onValueChange={(v) => handleChange('end_client', v || 'none')}
                          options={entityClients}
                          placeholder="Optional — search end client..."
                          data-testid="select-end-client"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Vendor Type first — so "New Vendor" is chosen before typing the name */}
                      <div className="space-y-2">
                        <Label htmlFor="vendor_type">Vendor Type *</Label>
                        <Select
                          value={['new', 'existing_new', 'new_client', 'old'].includes(formData.vendor_type) ? formData.vendor_type : 'old'}
                          onValueChange={(v) => {
                          handleChange('vendor_type', v);
                          // Keep any already-entered vendor/client name — do NOT clear.
                          if (v === 'new') {
                            handleChange('is_new_vendor_pending', true);
                          } else {
                            handleChange('is_new_vendor_pending', false);
                          }
                          if (v === 'new_client') {
                            handleChange('is_new_client_pending', true);
                          } else {
                            handleChange('is_new_client_pending', false);
                          }
                        }}>
                          <SelectTrigger data-testid="select-vendor-type">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New Vendor</SelectItem>
                            <SelectItem value="existing_new">Existing New Vendor</SelectItem>
                            <SelectItem value="new_client">New Client</SelectItem>
                            <SelectItem value="old">Old Vendor</SelectItem>
                          </SelectContent>
                        </Select>
                        {formData.vendor_type === 'new' && <p className="text-xs text-emerald-600">New Vendor bonus will apply if eligible (₹47,500 total)</p>}
                        {formData.vendor_type === 'existing_new' && <p className="text-xs text-cyan-600">Existing New Vendor bonus will apply (₹20,000 total)</p>}
                        {formData.vendor_type === 'new_client' && <p className="text-xs text-blue-600">New Client bonus will apply if eligible (₹1,00,000 total)</p>}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="vendor">Vendor *</Label>
                        {formData.vendor_type === 'new' ? (
                          <>
                            <Input
                              id="vendor"
                              value={formData.vendor}
                              onChange={(e) => handleChange('vendor', e.target.value)}
                              onBlur={(e) => handleChange('vendor', toTitleCase(e.target.value))}
                              placeholder="Type new vendor name"
                              data-testid="input-new-vendor"
                            />
                            <p className="text-[11px] text-amber-600">⚠ New vendor name pending Super Admin review. Once approved, it will be added to the vendor list.</p>
                          </>
                        ) : (
                          <>
                            <SearchSelect
                              id="vendor"
                              value={formData.vendor}
                              onValueChange={(v) => handleChange('vendor', v)}
                              onCreateOption={(name) => {
                                const titled = toTitleCase(name);
                                handleChange('vendor', titled);
                                handleChange('vendor_type', 'new');
                                handleChange('is_new_vendor_pending', true);
                              }}
                              options={entityVendors}
                              placeholder="Search vendor..."
                              allowCreate
                              createLabel="Add as new vendor"
                              minChars={2}
                              data-testid="select-vendor"
                            />
                            <p className="text-[11px] text-slate-500">Not in the list? Type the name and choose “Add as new vendor”, or set Vendor Type to New Vendor.</p>
                          </>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="client">End Client *</Label>
                        {formData.vendor_type === 'new_client' ? (
                          <>
                            <Input
                              id="client"
                              value={formData.client}
                              onChange={(e) => handleChange('client', e.target.value)}
                              onBlur={(e) => handleChange('client', toTitleCase(e.target.value))}
                              placeholder="Type new client name"
                              data-testid="input-new-client"
                            />
                            <p className="text-[11px] text-amber-600">⚠ New client name pending Super Admin review.</p>
                          </>
                        ) : (
                          <SearchSelect
                            id="client"
                            value={formData.client}
                            onValueChange={(v) => handleChange('client', v)}
                            onCreateOption={(name) => {
                              const titled = toTitleCase(name);
                              handleChange('client', titled);
                              handleChange('vendor_type', 'new_client');
                              handleChange('is_new_client_pending', true);
                            }}
                            options={entityClients}
                            placeholder="Search end client..."
                            allowCreate
                            createLabel="Add as new client"
                            data-testid="select-end-client-sales"
                          />
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Rate Info */}
              <div className="space-y-4 border-t border-slate-100 pt-6">
                <h3 className="font-semibold text-slate-900">Rate Information</h3>
                <p className="text-xs text-slate-500 -mt-2">Bill rate is what the client pays; pay rate is what the candidate receives. Margin = Bill Rate - Pay Rate. Incentives are based on margin.</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bill_rate">Bill Rate ($/hr) *</Label>
                    <Input
                      id="bill_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.bill_rate}
                      onChange={(e) => handleChange('bill_rate', e.target.value)}
                      placeholder="75.00"
                      required
                      data-testid="input-bill-rate"
                    />
                    {appliedDiscount && ((appliedDiscount.discount_pct || 0) > 0 || (appliedDiscount.discount_amount_per_hr || 0) > 0) && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg mt-1" data-testid="discount-notice">
                        <p className="text-sm text-amber-800 font-semibold">
                          {(appliedDiscount.discount_pct || 0) > 0
                            ? `${appliedDiscount.discount_pct}% Discount Applied`
                            : `$${Number(appliedDiscount.discount_amount_per_hr).toFixed(2)}/hr Discount Applied`}
                        </p>
                        <p className="text-xs text-amber-600 mt-0.5">
                          {appliedDiscount.company} / {appliedDiscount.vendor}{appliedDiscount.end_client ? ` / ${appliedDiscount.end_client}` : ''}
                        </p>
                        {parseFloat(formData.bill_rate) > 0 && (
                          <p className="text-sm text-amber-900 mt-1 font-bold">
                            FDR: ${effectiveFixedBillRate(parseFloat(formData.bill_rate), appliedDiscount).toFixed(2)}/hr
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pay_rate">Pay Rate ($/hr) *</Label>
                    <Input
                      id="pay_rate"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.pay_rate}
                      onChange={(e) => handleChange('pay_rate', e.target.value)}
                      placeholder="50.00"
                      required
                      data-testid="input-pay-rate"
                    />
                  </div>
                </div>

                {preview && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-2">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Info size={16} />
                      <span>Margin: <strong>${preview.margin}/hr</strong></span>
                      {((appliedDiscount?.discount_pct || 0) > 0 || (appliedDiscount?.discount_amount_per_hr || 0) > 0 || (discountData?.discount_pct || 0) > 0) && (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">
                          FDR applied {((appliedDiscount?.discount_pct || discountData?.discount_pct) > 0)
                            ? `(${appliedDiscount?.discount_pct || discountData?.discount_pct}% discount)`
                            : `($${Number(appliedDiscount?.discount_amount_per_hr || 0).toFixed(2)}/hr)`}
                        </span>
                      )}
                      {preview.isLowEnd && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">Low End</span>
                      )}
                    </div>
                    {formData.bill_rate > 0 && formData.pay_rate > 0 && (
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div className="p-2 bg-white rounded border border-slate-200 text-center">
                          <p className="text-xs text-slate-500">Candidate %</p>
                          <p className="text-sm font-bold text-slate-800" data-testid="candidate-percentage">
                            {(() => {
                              const bill = parseFloat(formData.bill_rate);
                              const fdr = effectiveFixedBillRate(bill, appliedDiscount || discountData);
                              return ((parseFloat(formData.pay_rate) / fdr) * 100).toFixed(1);
                            })()}%
                          </p>
                        </div>
                        <div className="p-2 bg-white rounded border border-slate-200 text-center">
                          <p className="text-xs text-slate-500">Company %</p>
                          <p className="text-sm font-bold text-slate-800" data-testid="company-percentage">
                            {(() => {
                              const bill = parseFloat(formData.bill_rate);
                              const fdr = effectiveFixedBillRate(bill, appliedDiscount || discountData);
                              return (((fdr - parseFloat(formData.pay_rate)) / fdr) * 100).toFixed(1);
                            })()}%
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Start Date, End Date & Placement Period */}
              <div className="space-y-4 border-t border-slate-100 pt-6">
                <h3 className="font-semibold text-slate-900">Dates & Placement Period</h3>
                <p className="text-xs text-slate-500 -mt-2">Start date is used for the 60-day approval window. Placement month/year determines which payroll cycle this falls under.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Candidate Start Date *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => handleChange('start_date', e.target.value)}
                      required
                      data-testid="input-start-date"
                    />
                    <p className="text-xs text-slate-500">
                      {isRecruitingModule ? 'Payout processed 60 days after this date.' : 'Mandatory for all placements.'}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="project_end_date">Project End Date</Label>
                    <Input
                      id="project_end_date"
                      type="date"
                      value={formData.project_end_date || ''}
                      onChange={(e) => handleChange('project_end_date', e.target.value)}
                      data-testid="input-project-end-date"
                    />
                    <p className="text-xs text-slate-500">Actual end date of the candidate's project.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="placement_month">Placement Month *</Label>
                    <Select value={String(formData.placement_month)} onValueChange={(v) => handleChange('placement_month', parseInt(v))}>
                      <SelectTrigger data-testid="select-placement-month">
                        <SelectValue placeholder="Month" />
                      </SelectTrigger>
                      <SelectContent>
                        {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                          <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="placement_year">Placement Year *</Label>
                    <Select value={String(formData.placement_year)} onValueChange={(v) => handleChange('placement_year', parseInt(v))}>
                      <SelectTrigger data-testid="select-placement-year">
                        <SelectValue placeholder="Year" />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Additional Options */}
              <div className="space-y-4 border-t border-slate-100 pt-6">
                <h3 className="font-semibold text-slate-900">Additional Options</h3>
                <p className="text-xs text-slate-500 -mt-2">Source portal, sourcing support, new technology multiplier.</p>

                {/* Sourced From — required dropdown */}
                <div className="space-y-2">
                  <Label htmlFor="sourced_from">Sourced From *</Label>
                  <p className="text-xs text-slate-400 mb-1">Select the portal/source from which this candidate was sourced.</p>
                  <Select value={formData.sourced_from} onValueChange={(v) => handleChange('sourced_from', v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="select-sourced-from"><SelectValue placeholder="Select source portal" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select source</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                      <SelectItem value="career_builder">Career Builder</SelectItem>
                      <SelectItem value="monster">Monster</SelectItem>
                      <SelectItem value="indeed">Indeed</SelectItem>
                      <SelectItem value="reference">Reference</SelectItem>
                      <SelectItem value="existing_employee">Existing Employee</SelectItem>
                    </SelectContent>
                  </Select>
                  {formData.sourced_from === 'reference' && (
                    <div className="mt-2 space-y-1">
                      <Label htmlFor="reference_note">Reference Details</Label>
                      <Input
                        value={formData.reference_note}
                        onChange={(e) => handleChange('reference_note', e.target.value)}
                        placeholder="Enter reference details (name, source, etc.)"
                        data-testid="input-reference-note"
                      />
                    </div>
                  )}
                </div>
                
                {!isRecruitingModule && !NO_SOURCING_CATEGORIES.includes(formData.category) && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="sourcing_support">Sourcing Support (Optional)</Label>
                      <p className="text-xs text-slate-400 mb-1">If a recruiter sourced this candidate, select them here — they will receive the sourcing recruiter share. <span className="font-medium text-slate-500">Select yourself if you also sourced the candidate (you'll receive both the Sales Recruiter and Sourcing Recruiter shares).</span></p>
                      <Select
                        value={formData.sourcing_support}
                        onValueChange={(v) => handleChange('sourcing_support', v === 'none' ? '' : v)}
                      >
                        <SelectTrigger data-testid="select-sourcing">
                          <SelectValue placeholder="Select team member" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No sourcing support</SelectItem>
                          {users.map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.name}{u.id === user?.id ? ' (You — also sourced)' : ''} ({u.role.replace(/_/g, ' ')})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="is_new_technology"
                          checked={formData.is_new_technology}
                          onCheckedChange={(checked) => {
                            handleChange('is_new_technology', checked);
                            if (!checked) handleChange('new_technology_name', '');
                          }}
                          data-testid="checkbox-new-tech"
                        />
                        <Label htmlFor="is_new_technology" className="cursor-pointer font-medium text-blue-800">
                          New Technology Placement (1.2x multiplier — requires admin approval)
                        </Label>
                      </div>
                      
                      {formData.is_new_technology && (
                        <div className="space-y-2 pl-6">
                          <Label htmlFor="new_technology_name" className="text-blue-700">Technology *</Label>
                          <Select
                            value={formData.new_technology_name}
                            onValueChange={(v) => handleChange('new_technology_name', v)}
                          >
                            <SelectTrigger data-testid="select-new-tech-name" className="bg-white">
                              <SelectValue placeholder="Select technology" />
                            </SelectTrigger>
                            <SelectContent>
                              {["Semiconductors", "Automotive", "Automation 4.0", "Battery", "AI/ML"].map(t => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-blue-600">1.2x multiplier will be applied after admin final approval.</p>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Split Share */}
              <div className="space-y-4">

              {/* External Support */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Support</h3>
                <p className="text-xs text-slate-500">Indicate if external support was used for this placement.</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="external_support">Support *</Label>
                    <Select value={formData.external_support} onValueChange={(val) => setFormData({ ...formData, external_support: val, ...(val === 'no' ? { external_support_amount: '', external_support_name: '', external_support_portal: '' } : {}) })}>
                      <SelectTrigger data-testid="select-external-support">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.external_support === 'yes' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="external_support_amount">Amount ($)</Label>
                        <Input
                          type="number"
                          id="external_support_amount"
                          value={formData.external_support_amount}
                          onChange={(e) => setFormData({ ...formData, external_support_amount: e.target.value })}
                          placeholder="e.g. 500"
                          data-testid="input-external-support-amount"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="external_support_name">External Support Name</Label>
                        <Input
                          type="text"
                          id="external_support_name"
                          value={formData.external_support_name}
                          onChange={(e) => setFormData({ ...formData, external_support_name: e.target.value })}
                          placeholder="Support person name"
                          data-testid="input-external-support-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="external_support_portal">Job Portal</Label>
                        <Input
                          type="text"
                          id="external_support_portal"
                          value={formData.external_support_portal}
                          onChange={(e) => setFormData({ ...formData, external_support_portal: e.target.value })}
                          placeholder="e.g. LinkedIn, Dice, Monster"
                          data-testid="input-external-support-portal"
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* RTR Upload */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">
                  {isRecruitingModule ? 'Candidate RTR (PDF) *' : 'Vendor RTR (PDF) *'}
                </h3>
                <div className="flex items-center gap-4">
                  <Input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => handleRtrUpload(e.target.files[0])}
                    disabled={rtrUploading}
                    className="flex-1"
                    data-testid="input-rtr-upload"
                  />
                  {rtrUploading && <SpinnerGap size={20} className="animate-spin text-blue-600" />}
                  {rtrUploaded && (
                    <span className="text-sm text-emerald-600 font-medium flex items-center gap-1">
                      <CheckCircle size={16} /> Uploaded
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  {isRecruitingModule
                    ? 'Upload the candidate RTR PDF before saving this placement.'
                    : 'Upload the vendor RTR PDF before saving this placement.'}
                </p>
              </div>

                <h3 className="font-semibold text-slate-900">Share Your Split</h3>
                <p className="text-xs text-slate-500 -mt-2">Share a percentage of your recruiter incentive with another team member. The split is deducted from your share.</p>
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg space-y-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="split_share_enabled"
                      checked={splitShare.enabled}
                      onCheckedChange={(checked) => {
                        setSplitShare({ ...splitShare, enabled: checked, user_id: '', percentage: '' });
                      }}
                      data-testid="checkbox-split-share"
                    />
                    <Label htmlFor="split_share_enabled" className="cursor-pointer font-medium text-slate-800">
                      Share a portion of my recruiter split with someone
                    </Label>
                  </div>

                  {splitShare.enabled && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-6">
                      <div className="space-y-2">
                        <Label>Share With *</Label>
                        <Select value={splitShare.user_id} onValueChange={(v) => setSplitShare({ ...splitShare, user_id: v })}>
                          <SelectTrigger data-testid="select-split-share-user">
                            <SelectValue placeholder="Select person" />
                          </SelectTrigger>
                          <SelectContent>
                            {users.filter(u => u.id !== user?.id).map((u) => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.name} ({u.role.replace(/_/g, ' ')})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Percentage to Share *</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="100"
                            value={splitShare.percentage}
                            onChange={(e) => setSplitShare({ ...splitShare, percentage: e.target.value })}
                            placeholder="e.g. 20"
                            data-testid="input-split-share-pct"
                          />
                          <span className="text-sm font-medium text-slate-600">%</span>
                        </div>
                        <p className="text-xs text-slate-500">
                          {splitShare.percentage ? `${splitShare.percentage}% of your recruiter share will go to the selected person` : 'Enter a percentage (1-100)'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Share Your Split — multiple recipients */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">Share Your Split</h3>
                    <p className="text-xs text-slate-500">Share a percentage of your incentive with other team members. Add multiple people.</p>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => setAdditionalSplits([...additionalSplits, { user_id: '', percentage: '' }])} data-testid="add-split-btn" className="text-xs">
                    + Add Person
                  </Button>
                </div>
                {additionalSplits.length > 0 && (
                  <div className="space-y-3">
                    {additionalSplits.map((split, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`additional-split-${idx}`}>
                        <div className="flex-1">
                          <Label className="text-xs mb-1">Person</Label>
                          <Select value={split.user_id} onValueChange={(v) => {
                            const updated = [...additionalSplits];
                            updated[idx].user_id = v;
                            setAdditionalSplits(updated);
                          }}>
                            <SelectTrigger data-testid={`split-user-${idx}`}><SelectValue placeholder="Select person" /></SelectTrigger>
                            <SelectContent>
                              {allUsers.filter(u => u.id !== user?.id).map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.name} ({u.role?.replace(/_/g, ' ')})</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-24">
                          <Label className="text-xs mb-1">%</Label>
                          <Input type="number" min="1" max="100" value={split.percentage}
                            onChange={(e) => {
                              const updated = [...additionalSplits];
                              updated[idx].percentage = e.target.value;
                              setAdditionalSplits(updated);
                            }}
                            placeholder="%" className="h-9" data-testid={`split-pct-${idx}`} />
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setAdditionalSplits(additionalSplits.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700 mt-4" data-testid={`remove-split-${idx}`}>
                          Remove
                        </Button>
                      </div>
                    ))}
                    {additionalSplits.length > 0 && (
                      <p className="text-xs text-slate-500">
                        Total shared: {additionalSplits.reduce((s, sp) => s + (parseFloat(sp.percentage) || 0), 0)}% of your recruiter share
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* 60-Day Completion */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">60-Day Completion Status</h3>
                <p className="text-xs text-slate-500 -mt-2">If the candidate has already completed 60 days, select "Yes" to bypass the waiting period and proceed directly to Lead/Manager approval.</p>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                  <div className="space-y-2">
                    <Label>Has the candidate completed 60 days? *</Label>
                    <Select value={formData.sixty_day_completed} onValueChange={(v) => handleChange('sixty_day_completed', v)}>
                      <SelectTrigger data-testid="sixty-day-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes - Completed 60 days</SelectItem>
                        <SelectItem value="no">No - Not yet completed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.sixty_day_completed === 'yes' && (
                    <div className="p-2 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-700" data-testid="sixty-day-yes-msg">
                      Candidate has completed 60 days. Upon submission, this placement will proceed directly to Lead for review, then Manager for final approval.
                    </div>
                  )}
                  {formData.sixty_day_completed === 'no' && (
                    <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700" data-testid="sixty-day-no-msg">
                      Candidate has not completed 60 days. Approval will be held until the 60-day period is met.
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Notes</h3>
                <div className="space-y-2">
                  <Label htmlFor="submission_notes">Submission Notes (Optional)</Label>
                  <Textarea
                    id="submission_notes"
                    value={formData.submission_notes}
                    onChange={(e) => handleChange('submission_notes', e.target.value)}
                    placeholder="Any additional notes about this placement..."
                    rows={3}
                    data-testid="input-submission-notes"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-200">
                <Button
                  type="button"
                  variant="outline"
                  disabled={loading}
                  onClick={(e) => handleSubmit(e, true)}
                  data-testid="save-draft-btn"
                >
                  {loading ? <SpinnerGap size={20} className="animate-spin mr-2" /> : null}
                  Save as Draft
                </Button>
                <Button
                  type="button"
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  onClick={(e) => handleSubmit(e, false)}
                  data-testid="submit-btn"
                >
                  {loading ? <SpinnerGap size={20} className="animate-spin mr-2" /> : null}
                  Save & Submit
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Preview Panel */}
        <Card className="border-slate-200 h-fit sticky top-24">
          <CardHeader>
            <CardTitle className="text-lg font-['Outfit']">Incentive Preview</CardTitle>
            <CardDescription>Exact payout based on category & margin. Amounts update in real-time as you change inputs.</CardDescription>
          </CardHeader>
          <CardContent>
            {preview ? (
              <div className="space-y-4">
                {/* Total Placement - SA/CEO only */}
                {(isSuperadmin || isCeo) && (
                  <div className="p-4 bg-emerald-50 rounded-lg text-center">
                    <p className="text-xs text-emerald-600 uppercase font-semibold">Total Placement Incentive</p>
                    <p className="text-3xl font-bold text-emerald-700" data-testid="preview-total">
                      {formatCurrency(preview.estimatedTotal)}
                    </p>
                    <p className="text-xs text-emerald-500 mt-1">Margin: ${preview.margin}/hr — {fmtUSD(Math.round(preview.estimatedTotal / 83.5))}</p>
                  </div>
                )}

                {/* Your Estimated Payout */}
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600">Your Payout (Direct)</p>
                  {(() => {
                    const isSelfSourced = formData.sourcing_support && formData.sourcing_support === user?.id;
                    const ownSourcingShare = isSelfSourced ? (preview.splits?.sourcing_recruiter || 0) : 0;
                    let basePayout = isRecruitingModule
                      ? (preview.splits?.recruiter || 0)
                      : ((preview.splits?.sales_recruiter || preview.totalDirect || 0) + ownSourcingShare);
                    const splitPct = splitShare.enabled && splitShare.percentage ? parseFloat(splitShare.percentage) : 0;
                    // Split-share % only deducts from the sales_recruiter portion, not the self-sourced share
                    const sharablePortion = basePayout - ownSourcingShare;
                    const afterSplit = splitPct > 0 ? (Math.round(sharablePortion * (1 - splitPct / 100)) + ownSourcingShare) : basePayout;
                    return (
                      <>
                        <p className="text-xl font-bold text-blue-700" data-testid="preview-your-payout">
                          {formatCurrency(afterSplit)}
                        </p>
                        {isSelfSourced && ownSourcingShare > 0 && (
                          <p className="text-[10px] text-blue-500 mt-0.5">{formatCurrency(basePayout - ownSourcingShare)} (Sales Recruiter) + {formatCurrency(ownSourcingShare)} (Sourcing Recruiter)</p>
                        )}
                        {splitPct > 0 && (
                          <p className="text-[10px] text-blue-500 mt-0.5">After {splitPct}% split: {formatCurrency(basePayout)} → {formatCurrency(afterSplit)}</p>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Total Direct (for existing categories where sr=0) */}
                {!isRecruitingModule && preview.totalDirect > 0 && preview.splits?.sales_recruiter === 0 && (
                  <div className="p-2 bg-slate-50 rounded-lg text-center">
                    <p className="text-xs text-slate-500">Total Direct (to creator)</p>
                    {(() => {
                      const splitPct = splitShare.enabled && splitShare.percentage ? parseFloat(splitShare.percentage) : 0;
                      const adjusted = splitPct > 0 ? Math.round(preview.totalDirect * (1 - splitPct / 100)) : preview.totalDirect;
                      return <p className="text-lg font-bold text-slate-700">{formatCurrency(adjusted)}</p>;
                    })()}
                  </div>
                )}

                {/* Full Split Breakdown */}
                {preview.splits && Object.keys(preview.splits).length > 0 && (
                  <div className="pt-3 border-t border-slate-200">
                    <p className="text-xs font-semibold text-slate-500 mb-1 uppercase">Split Breakdown (₹)</p>
                    <p className="text-[10px] text-slate-400 mb-2">How the total placement incentive is distributed across roles. Split share deductions are reflected in real-time.</p>
                    <div className="space-y-1.5">
                      {(() => {
                        const splitPct = splitShare.enabled && splitShare.percentage ? parseFloat(splitShare.percentage) : 0;
                        const splitRecipientName = splitShare.user_id ? allUsers.find(u => u.id === splitShare.user_id)?.name : null;
                        const isAdmin = isSuperadmin || isCeo;
                        if (isRecruitingModule) {
                          const baseRecruiter = preview.splits.recruiter || 0;
                          const splitAmount = splitPct > 0 ? Math.round(baseRecruiter * splitPct / 100) : 0;
                          const adjustedRecruiter = baseRecruiter - splitAmount;
                          return (
                            <>
                              <SplitRow label="Recruiter" amount={adjustedRecruiter} name={user?.name} color="blue" />
                              {splitAmount > 0 && (
                                isAdmin
                                  ? <SplitRow label="Split Partner" amount={splitAmount} name={splitRecipientName} color="violet" />
                                  : <SplitRow label="Split Partner" name={splitRecipientName} color="violet" showAmount={false} pctLabel={`${splitPct}% share`} />
                              )}
                              {isAdmin && <SplitRow label="Lead" amount={preview.splits.lead} name={teamInfo.lead?.name} color="purple" />}
                              {isAdmin && <SplitRow label="Manager" amount={preview.splits.manager} name={teamInfo.manager?.name} color="indigo" />}
                            </>
                          );
                        } else {
                          const baseSR = preview.splits.sales_recruiter || 0;
                          const creatorBase = baseSR > 0 ? baseSR : (preview.totalDirect || 0);
                          const splitAmount = splitPct > 0 ? Math.round(creatorBase * splitPct / 100) : 0;
                          const adjustedCreator = creatorBase - splitAmount;
                          const sourcingName = formData.sourcing_support ? allUsers.find(u => u.id === formData.sourcing_support)?.name : null;
                          const isSelfSourced = formData.sourcing_support && formData.sourcing_support === user?.id;
                          return (
                            <>
                              <SplitRow label={isAdmin ? "Sales Recruiter" : "Your Share"} amount={adjustedCreator} name={user?.name} color="blue" />
                              {splitAmount > 0 && (
                                isAdmin
                                  ? <SplitRow label="Split Partner" amount={splitAmount} name={splitRecipientName} color="violet" />
                                  : <SplitRow label="Split Partner" name={splitRecipientName} color="violet" showAmount={false} pctLabel={`${splitPct}% share`} />
                              )}
                              {formData.sourcing_support && sourcingName && (
                                (isAdmin || isSelfSourced)
                                  ? <SplitRow label={isSelfSourced ? "Sourcing Recruiter (You)" : "Sourcing Recruiter"} amount={preview.splits.sourcing_recruiter} name={sourcingName} color="teal" />
                                  : <SplitRow label="Sourcing Recruiter" name={sourcingName} color="teal" showAmount={false} pctLabel="Added" />
                              )}
                              {isAdmin && <SplitRow label="Sales Lead" amount={preview.splits.sales_lead} name={teamInfo.lead?.name} color="purple" />}
                              {isAdmin && <SplitRow label="Sales Manager" amount={preview.splits.sales_manager} name={teamInfo.manager?.name} color="indigo" />}
                              {isAdmin && formData.sourcing_support && (preview.splits.sourcing_lead || 0) > 0 && <SplitRow label="Sourcing Lead" amount={preview.splits.sourcing_lead} name={teamInfo.sourcingLead?.name} color="cyan" />}
                              {isAdmin && formData.sourcing_support && (preview.splits.sourcing_manager || 0) > 0 && <SplitRow label="Sourcing Manager" amount={preview.splits.sourcing_manager} name={teamInfo.sourcingManager?.name} color="sky" />}
                            </>
                          );
                        }
                      })()}
                    </div>
                  </div>
                )}

                {preview.isFlat && (
                  <div className="text-xs text-center text-teal-600 font-medium">Flat Incentive</div>
                )}
                {formData.is_new_technology && (
                  <div className="text-xs text-center text-blue-600 font-medium">Preview is the category amount. 1.2x New Technology applies only after admin approval.</div>
                )}
                {preview.isLowEnd && (
                  <div className="text-xs text-center text-amber-600 font-medium">Low End Rate (Bill ≤ $40/hr)</div>
                )}

                {/* New Vendor / New Client Staged Bonus Preview — Sales module only.
                    Shown when:
                      • vendor is already owned (we show owner / owner-followup / owned-by-other)
                      • OR vendor_type='new'/'existing_new'/'new_client' (Stage 1 unestablished). */}
                {!isRecruitingModule && parseFloat(formData.bill_rate) >= 50 && (
                  (() => {
                    const claim = formData.vendor_type === 'new_client' ? clientClaim : vendorClaim;
                    if (!claim) return null;
                    const isOwned = !!claim.owner_id;
                    const isFirstPlaceTrigger = ['new', 'existing_new', 'new_client'].includes(formData.vendor_type);
                    // No badge if the vendor is unowned AND the user didn't pick a "new" vendor type.
                    if (!isOwned && !isFirstPlaceTrigger) return null;
                    // Decide whose perspective the placement is "for" (SA/CEO proxy support).
                    const effectiveCreatorId = (isSuperadmin || isCeo) ? formData.created_for_user_id : user?.id;
                    const willGetBonus = !isOwned || (isOwned && claim.owner_id === effectiveCreatorId && claim.next_stage);
                    const dots = (used, total = 6) => Array.from({ length: total }).map((_, i) => i < used ? '●' : '○').join(' ');
                    if (isOwned && !willGetBonus) {
                      return (
                        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid="vendor-bonus-preview-owned-by-other">
                          <p className="text-xs font-semibold text-slate-600 uppercase mb-1">{formData.vendor_type === 'new_client' ? 'New Client Bonus' : 'New Vendor Bonus'}</p>
                          <p className="text-sm text-slate-700">This {formData.vendor_type === 'new_client' ? 'client' : 'vendor'} was introduced by <strong>{claim.owner_name}</strong> — Stage {claim.stages_used}/6 used.</p>
                          <p className="text-[11px] text-slate-500 mt-0.5">No Stage Bonus on this placement (bonus belongs to the original introducer).</p>
                          <p className="text-[10px] text-slate-400 mt-0.5 font-mono">{dots(claim.stages_used)}</p>
                        </div>
                      );
                    }
                    if (!claim.next_stage) {
                      return (
                        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                          <p className="text-xs text-slate-600">All 6 stages used for this {formData.vendor_type === 'new_client' ? 'client' : 'vendor'} — no further New {formData.vendor_type === 'new_client' ? 'Client' : 'Vendor'} Bonus.</p>
                        </div>
                      );
                    }
                    const eff = claim.effective_type;
                    const totalCap = eff === 'new_client' ? '₹1,00,000 (₹25K + 5×₹15K)'
                      : eff === 'existing_new' ? '₹20,000 (₹5K + 5×₹3K)'
                      : '₹47,500 (₹10K + 5×₹7.5K)';
                    const label = eff === 'new_client' ? 'New Client Bonus'
                      : eff === 'existing_new' ? 'Existing New Vendor Bonus'
                      : 'New Vendor Bonus';
                    const pretext = isOwned ? `🎯 You own this ${eff === 'new_client' ? 'client' : 'vendor'} — ` : '';
                    return (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="vendor-bonus-preview">
                        <p className="text-xs font-semibold text-emerald-700 uppercase mb-1">{label}</p>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-emerald-700">{pretext}Stage {claim.next_stage} of 6 — You earn ₹{(claim.next_amount || 0).toLocaleString('en-IN')}</p>
                            <p className="text-[10px] text-emerald-500 mt-0.5">Total over 6 placements: {totalCap}</p>
                            <p className="text-[10px] text-emerald-600 mt-0.5 font-mono">Progress: {dots(claim.stages_used)} ({claim.stages_used}/6 used)</p>
                            <p className="text-[10px] text-emerald-400 mt-0.5">Not applicable for Low-End rates (bill &lt; $50/hr)</p>
                          </div>
                          <p className="text-xl font-bold text-emerald-800">₹{(claim.next_amount || 0).toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                    );
                  })()
                )}

                {isRecruitingModule && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700">
                      Payout after 60 days from candidate start date. Requires Lead then Manager approval.
                    </p>
                  </div>
                )}

                <p className="text-xs text-slate-400 pt-4 border-t border-slate-200">
                  Final amounts calculated upon submission and may vary based on admin rules.
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-500">
                Enter bill rate, pay rate, and category to see estimated incentives.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NewPlacementPage;
